import React from 'react'

interface ConfirmationCardProps {
  price?: number
  deliveryTime?: number
  comment?: string
  priceFormatted?: string
  deliveryTimeFormatted?: string
  state: 'pending' | 'accepted' | 'declined' | 'modified'
  isLoading?: boolean
  onAccept: () => void
  onModify: () => void
  // Информация о дедлайне
  deadlineFormatted?: string
  deadlineStatus?: string
  deadlineCssClass?: string
  daysRemaining?: number
  // Статус заказа для определения стилей
  status?: string
}

export const ConfirmationCard: React.FC<ConfirmationCardProps> = ({
  price,
  deliveryTime,
  comment,
  priceFormatted,
  deliveryTimeFormatted,
  state,
  isLoading = false,
  onAccept,
  onModify,
  // Информация о дедлайне
  deadlineFormatted,
  deadlineStatus,
  deadlineCssClass,
  daysRemaining,
  // Статус заказа
  status
}) => {
  // Форматируем цену если не передана готовая строка
  const displayPrice = priceFormatted || (price ? `${price.toLocaleString('ru-RU')} руб.` : null)
  
  // Форматируем срок если не передана готовая строка
  const displayDeliveryTime = deliveryTimeFormatted || (deliveryTime ? 
    `${deliveryTime} рабочий${deliveryTime === 1 ? ' день' : deliveryTime < 5 ? ' дня' : ' дней'}` : null)

  // Определяем заголовок и стиль в зависимости от состояния
  const getCardContent = () => {
    switch (state) {
      case 'accepted':
        return {
          title: 'Принял условия',
          titleColor: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          icon: '✅',
          showButtons: false
        }
      case 'declined':
        return {
          title: 'Отклонил условия',
          titleColor: 'text-red-700',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          icon: '❌',
          showButtons: false
        }
      case 'modified':
        return {
          title: 'Запросил изменение',
          titleColor: 'text-orange-700',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          icon: '✏️',
          showButtons: false
        }
      default: // pending
        return {
          title: 'Подтверждаете условия?',
          titleColor: 'text-blue-700',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          icon: '❓',
          showButtons: true
        }
    }
  }

  const cardContent = getCardContent()

  // Функция для определения цвета текста дедлайна
  const getDeadlineTextColor = () => {
    if (!daysRemaining && daysRemaining !== 0) return 'text-gray-800'
    
    if (daysRemaining < 0) return 'text-red-600'      // Просрочено
    if (daysRemaining === 0) return 'text-orange-600' // Сегодня
    if (daysRemaining <= 3) return 'text-red-500'     // Срочно
    if (daysRemaining <= 7) return 'text-yellow-600'  // Предупреждение
    return 'text-green-600'                           // Нормально
  }

  if (!displayPrice && !displayDeliveryTime) {
    return null
  }

  return (
    <div className={`${cardContent.bgColor} rounded-xl shadow-sm p-3 sm:p-4 mb-4 border ${cardContent.borderColor} transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-center mb-3">
        <h3 className={`text-base sm:text-lg font-semibold ${cardContent.titleColor} flex items-center`}>
          <span className="mr-2 text-lg sm:text-xl">{cardContent.icon}</span>
          {cardContent.title}
        </h3>
      </div>

      <div className="space-y-2 text-sm sm:text-base text-gray-700">
        {displayPrice && (
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-600">Цена:</span>
            <span className="font-bold text-gray-900">{displayPrice}</span>
          </div>
        )}
        
        {displayDeliveryTime && (
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-600">Срок:</span>
            <span className="text-gray-800">{displayDeliveryTime}</span>
          </div>
        )}
        
        {/* Отображение дедлайна когда есть дедлайн */}
        {deadlineFormatted && (
          <div className={`flex justify-between items-center ${(status === 'completed' || status === 'rejected') ? 'line-through text-gray-400' : ''}`}>
            <span className="font-medium text-gray-600">
              Сдать до:
            </span>
            <span className={`font-semibold ${(status === 'completed' || status === 'rejected') ? 'text-gray-400' : getDeadlineTextColor()}`}>
              {deadlineFormatted}
              {deadlineStatus && deadlineStatus !== deadlineFormatted && (
                <span className="ml-1 text-xs">({deadlineStatus})</span>
              )}
            </span>
          </div>
        )}
      </div>

      {comment && (
        <div className={`mt-3 pt-3 border-t ${cardContent.borderColor}`}>
          <p className="font-medium text-gray-600 text-xs sm:text-sm mb-1">Комментарий:</p>
          <p className="text-gray-700 text-xs sm:text-sm italic">{comment}</p>
        </div>
      )}

      {cardContent.showButtons && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            onClick={onAccept}
            disabled={isLoading}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm sm:text-base flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Обработка...
              </>
            ) : (
              '✅ Принять'
            )}
          </button>
          <button
            onClick={onModify}
            disabled={isLoading}
            className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm sm:text-base"
          >
            ✏️ Изменить
          </button>
        </div>
      )}
    </div>
  )
}

export default ConfirmationCard
